/**
 * leaf_transmission.ts — how much light a species' leaves let through, per species (surfaces, 2026-09-27, research §5.3). Pure.
 *
 * The foliage translucency (engine `applyFoliageTranslucency`) glows a back-lit crown by `backIntensity` (0.55 in
 * EnvironmentModule) for EVERY tree species alike. Real leaves differ by thickness: a thin broadleaf (birch, willow)
 * transmits most of the green it does not absorb, a sclerophyll leaflet (acacia) or a waxy palm frond less, a conifer
 * needle least — a back-lit pine reads as a dark crown with a bright RIM (light through the gaps, not the needles).
 *
 * The brief's idea — a thickness from the atlas' alpha coverage — has no signal here: the generated trees are OPAQUE
 * solid meshes (Meshy / Hunyuan crowns are geometry, not alpha cards), and the geometric proxy (crown surface area over a
 * sphere) ranks the acacia densest and the pine sparsest (measured on the cooked LOD0s: 2.40 / 1.14 palm / 0.79 pine) — the
 * reverse of the leaves. So the thickness is a per-SPECIES table read from the key, calibrated against back-lit pictures
 * of a palm, an acacia and a pine at 16:00 (`verify/leaf-species-probe.mjs`, the `__leafThickness` lever).
 *
 * CALIBRATION (2026-09-28, WebGPU potato, camera → tree → sun; the crown's own cells over the sky beside it, k 0 / 0.2 /
 * 0.35 / 0.5 / 1): palm 0.49 / 0.88 / 0.93 / 0.97 / 1.05, acacia 0.54 / 0.62 / 0.66 / 0.68 / 0.74, pine 0.43 / 0.44 /
 * 0.45 / 0.46 / 0.47. LOOKED AT: at k 1 the palm crown goes milky (brighter than the sky beside it) and the acacia's a
 * washed cream — the frond and leaflet structure is lost; the palm reads right at 0.2–0.35 (a green glow, fronds kept),
 * the acacia at 0.35 (yellow-green; pale by 0.5), the pine dark-with-glow at 0.2. Broadleaf has no back-lit picture this
 * round; it stays at 1, the look CHECKPOINT 5 pictured and kept (birch tufts yellow-green, not washed) — nothing
 * unpictured is raised.
 *
 * The value multiplies `backIntensity`: 1 = the pre-table look. An unknown / fantasy species (fungal, crystal) keeps 1.
 */

/** The groups, in order of the first rule that matches the species key (a substring of `foliage:<name>_tree`). */
export const LEAF_GROUPS: ReadonlyArray<{ group: string; words: readonly string[]; k: number }> = [
  // needles: thick, few photons through the leaf itself
  { group: 'conifer', words: ['pine', 'cedar', 'juniper', 'spruce', 'fir'], k: 0.2 },
  // palm fronds: broad but waxy and thick
  { group: 'palm', words: ['palm'], k: 0.3 },
  // sclerophyll leaflets: small, thin-ish, cuticled
  { group: 'sclerophyll', words: ['acacia'], k: 0.4 },
  // thin mesophytic broadleaves: the most translucent (kept at the pictured pre-table look until a back-lit picture says more)
  { group: 'broadleaf', words: ['birch', 'willow', 'copperleaf', 'paperbark', 'mangrove', 'maple', 'oak', 'orchard', 'shade'], k: 1 },
];

/** The multiplier on the translucency's back intensity for a species key (1 = unchanged). */
export function leafTransmissionFor(key: string | null | undefined): number {
  const k = (key ?? '').toLowerCase();
  if (!k) return 1;
  for (const g of LEAF_GROUPS) if (g.words.some((w) => k.includes(w))) return g.k;
  return 1;
}

/** The group a species key falls in ('' = none, the unchanged look). */
export function leafGroupOf(key: string | null | undefined): string {
  const k = (key ?? '').toLowerCase();
  for (const g of LEAF_GROUPS) if (g.words.some((w) => k.includes(w))) return g.group;
  return '';
}
